import React, { useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import API_ENDPOINTS from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import EmailOtpForm from '../auth/EmailOtpForm';
import PinLoginForm from '../auth/PinLoginForm';
import KuutioCubeGradient from '../KuutioCubeGradient';
import { getDeniedReason, getLinkedPin } from '../../utils/pinSession';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  width: 100vw;
  min-height: 100vh;
  background: #000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const LoginCard = styled.div`
  background: linear-gradient(180deg, #000 0%, #111 100%);
  border: 1px solid #222;
  border-radius: 4px;
  padding: 48px 40px;
  width: 100%;
  max-width: 400px;
  box-sizing: border-box;
  animation: ${fadeIn} 0.5s ease-out;
`;

const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 32px;
`;

/* The same WebGL cube as the landing page and /me: it starts in the logo's isometric pose, then tumbles. */
const LogoCube = styled.div`
  width: 140px;
  height: 140px;
  margin: -16px 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #888;
  margin: 0;
  line-height: 1.5;
`;

const Notice = styled.div`
  color: #ef4444;
  font-size: 14px;
  text-align: center;
  padding: 12px;
  margin-bottom: 16px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid #333;
  border-radius: 4px;
`;

const Choices = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Choice = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  background: #0a0a0a;
  border: 1px solid #222;
  border-radius: 4px;
  color: #fff;
  font: inherit;
  font-size: 15px;
  line-height: 1.4;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;

  &::after {
    content: '→';
    color: #555;
    flex-shrink: 0;
  }

  &:hover, &:focus-visible {
    outline: none;
    background: #111;
    border-color: #3949ab;
  }
`;

const StaffButton = styled.a`
  display: block;
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #1a237e 0%, #4a148c 100%);
  border: 1px solid #3949ab;
  border-radius: 4px;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  box-sizing: border-box;
  box-shadow: 0 0 20px rgba(74, 20, 140, 0.4);

  &:hover {
    background: linear-gradient(135deg, #283593 0%, #6a1b9a 100%);
    border-color: #5c6bc0;
  }
`;

const BackButton = styled.button`
  display: block;
  margin: 24px auto 0;
  background: none;
  border: none;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  text-decoration: underline;
  &:hover { color: #aaa; }
`;

// The three ways in, in the order the card lists them (#1116)
const CHOICES = [
  { view: 'email', label: 'pinLogin.chooseEmail' },
  { view: 'pin', label: 'pinLogin.choosePin' },
  { view: 'staff', label: 'pinLogin.chooseStaff' },
];

/** Microsoft sign-in for Sitra staff, landing on the instance list (as screens/LoginScreen.jsx builds it). */
function staffSignInUrl() {
  const origin = encodeURIComponent(window.location.origin);
  return `${API_ENDPOINTS.AUTH_ENTRA_LOGIN()}?return_to=${encodeURIComponent('/instances')}&return_to_origin=${origin}`;
}

/**
 * AdminLogin - the front page's sign-in: three ways in, one view each
 *
 * The card opens on the choice: an invitation by email (the code, → /me), a
 * workshop PIN, or Sitra staff (Microsoft). Picking one shows only that way
 * in and a back link. A PIN link (/?pin=…) opens the PIN view and signs in;
 * a link AuthGate refused (/?denied=…) says why above the choice.
 *
 * @param {function} onLogin - Called with { instanceId, isAdmin, session } after a PIN sign-in
 * @param {'choose'|'email'|'pin'|'staff'} [initialView] - The view to open on (stories)
 * @param {'signin'|'instance'|'expired'} [denied] - Why the link was refused; read from ?denied= when not given
 */
const AdminLogin = ({ onLogin, initialView, denied: deniedReason }) => {
  const { t } = useTranslation();
  const [linkedPin] = useState(getLinkedPin);
  const [denied] = useState(() => deniedReason ?? getDeniedReason());
  const [view, setView] = useState(initialView || (linkedPin ? 'pin' : 'choose'));
  const choice = CHOICES.find((c) => c.view === view);

  return (
    <Container>
      <LoginCard>
        <LogoSection>
          <LogoCube aria-hidden="true">
            <KuutioCubeGradient size={140} />
          </LogoCube>
          <Title>{t('pinLogin.title')}</Title>
          <Subtitle>{choice ? t(choice.label) : t('pinLogin.chooseSubtitle')}</Subtitle>
        </LogoSection>

        {view === 'choose' && (
          <>
            {denied && <Notice role="alert">{t(`pinLogin.denied.${denied}`)}</Notice>}
            <Choices>
              {CHOICES.map((c) => (
                <Choice key={c.view} type="button" data-testid={`login-choice-${c.view}`} onClick={() => setView(c.view)}>
                  {t(c.label)}
                </Choice>
              ))}
            </Choices>
          </>
        )}

        {view === 'email' && <EmailOtpForm redirectTo="/me" />}
        {view === 'pin' && <PinLoginForm onLogin={onLogin} linkedPin={linkedPin} />}
        {view === 'staff' && <StaffButton href={staffSignInUrl()}>{t('pinLogin.staffSignIn')}</StaffButton>}

        {view !== 'choose' && (
          <BackButton type="button" data-testid="login-back" onClick={() => setView('choose')}>
            ← {t('pinLogin.back')}
          </BackButton>
        )}
      </LoginCard>
    </Container>
  );
};

export default AdminLogin;
